import { getAddress, keccak256, stringToHex, toHex } from 'viem';
import type { Address, Hex } from 'viem';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type {
    SelfSignedTradingSessionProof,
    TradingSessionStatement,
} from '@/lib/api/tradingSession';

import {
    SNAPPY_TRADING_SESSION_TYPES,
    tradingSessionDomain,
} from './protocol';

/** Only window the signer accepts for a bootstrap statement. */
export const BOOTSTRAP_SESSION_WINDOW_SECONDS = 300;

export interface BootstrapSessionTypedData {
    domain: {
        name: 'SnappyTradingSession';
        version: '1';
        chainId: AnchorChainId;
        verifyingContract: Address;
    };
    types: typeof SNAPPY_TRADING_SESSION_TYPES;
    primaryType: 'TradingSession';
    message: {
        sessionId: Hex;
        owner: Address;
        authorizationWallet: Address;
        tradingWallet: Address;
        audienceHash: Hex;
        originHash: Hex;
        issuedAt: number;
        expiresAt: number;
    };
}

export type BootstrapTradingSessionProof = SelfSignedTradingSessionProof;

export function randomSessionId(): Hex {
    return toHex(crypto.getRandomValues(new Uint8Array(32)));
}

/**
 * The self-signed statement that authorizes creating a Snappy account for a
 * wallet-only user. It is deliberately unusable as a real trading session: both
 * wallet fields are the manager address itself, and a factory-deployed custodial
 * wallet can never be the manager, so nothing this statement names can trade.
 *
 * One statement per manager. The manager is the EIP-712 `verifyingContract`
 * (RustSigner passes the association's own manager to the verifier), so an
 * anchor statement cannot be replayed against HyperEVM or the other way around.
 */
export function createBootstrapSessionStatement(input: {
    owner: Address;
    manager: Address;
    origin: string;
    issuedAt: number;
    sessionId: Hex;
}): TradingSessionStatement {
    const manager = getAddress(input.manager);
    return {
        protocol: 'snappy-trading-session',
        version: 1,
        sessionId: input.sessionId,
        owner: getAddress(input.owner),
        authorizationWallet: manager,
        tradingWallet: manager,
        audience: 'api.wolfswap.app',
        origin: input.origin,
        issuedAt: input.issuedAt,
        expiresAt: input.issuedAt + BOOTSTRAP_SESSION_WINDOW_SECONDS,
    };
}

/**
 * `domain.chainId` is the ANCHOR even for the HyperEVM leg: the anchor is what
 * scopes a user's identity, while `verifyingContract` is the manager the
 * statement actually targets. RustSigner verifies exactly this pair.
 */
export function createBootstrapSessionTypedData(
    statement: TradingSessionStatement,
    manager: Address,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): BootstrapSessionTypedData {
    return {
        domain: {
            ...tradingSessionDomain(anchorChainId),
            verifyingContract: getAddress(manager),
        },
        types: SNAPPY_TRADING_SESSION_TYPES,
        primaryType: 'TradingSession',
        message: {
            sessionId: statement.sessionId as Hex,
            owner: getAddress(statement.owner),
            authorizationWallet: getAddress(statement.authorizationWallet),
            tradingWallet: getAddress(statement.tradingWallet),
            audienceHash: keccak256(stringToHex(statement.audience)),
            originHash: keccak256(stringToHex(statement.origin)),
            issuedAt: statement.issuedAt,
            expiresAt: statement.expiresAt,
        },
    };
}
